import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, FileText, Eye, Download, Share2, IndianRupee } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';

const inputCls = 'w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rozops-blue/20 focus:border-rozops-blue';

export default function Billing() {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showGen, setShowGen] = useState(false);
  const [selectedInv, setSelectedInv] = useState(null);

  const today = new Date().toISOString().slice(0, 10);
  const monthStart = new Date().toISOString().slice(0, 7) + '-01';
  const [form, setForm] = useState({ customer_id: '', billing_start_date: monthStart, billing_end_date: today });

  const load = async () => {
    const [i, c] = await Promise.all([apiGet('/merchant/invoices'), apiGet('/merchant/customers')]);
    setInvoices(i.data || []);
    setCustomers(c.data || []);
  };
  useEffect(() => { load().catch(e => toast.error(e.message)); }, []);

  const generate = async (e) => {
    e.preventDefault();
    try {
      await apiPost('/merchant/invoices/generate', form);
      toast.success('Invoice generated');
      setShowGen(false);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const view = async (iid) => {
    const r = await apiGet(`/merchant/invoices/${iid}`);
    setSelectedInv(r.data);
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const generatePdf = async () => {
    if (!selectedInv) return;
    setPdfBusy(true);
    try {
      const r = await apiPost(`/merchant/invoices/${selectedInv.invoice.id}/pdf`);
      window.open(r.data.pdf_url, '_blank');
      setSelectedInv({ ...selectedInv, invoice: { ...selectedInv.invoice, pdf_url: r.data.pdf_url, whatsapp_url: r.data.whatsapp_url } });
      toast.success('PDF ready');
    } catch (err) { toast.error(err.message); } finally { setPdfBusy(false); }
  };

  const shareWhatsapp = async () => {
    if (!selectedInv) return;
    let url = selectedInv.invoice.whatsapp_url;
    if (!url) {
      const r = await apiPost(`/merchant/invoices/${selectedInv.invoice.id}/pdf`);
      url = r.data.whatsapp_url;
      setSelectedInv({ ...selectedInv, invoice: { ...selectedInv.invoice, pdf_url: r.data.pdf_url, whatsapp_url: url } });
    }
    window.open(url, '_blank');
  };

  const collectRazorpay = async () => {
    if (!selectedInv) return;
    if (Number(selectedInv.invoice.due_amount) <= 0) return toast.info('Nothing due');
    try {
      const r = await apiPost('/merchant/razorpay/order', { invoice_id: selectedInv.invoice.id });
      const { order, key_id, amount, customer, invoice_number } = r.data;
      const options = {
        key: key_id,
        amount,
        currency: 'INR',
        name: 'RozOps',
        description: `Invoice ${invoice_number}`,
        order_id: order.id,
        prefill: {
          name: customer?.full_name || '',
          contact: customer?.phone || '',
          email: customer?.email || '',
        },
        theme: { color: '#5B21F4' },
        handler: async (resp) => {
          try {
            await apiPost('/merchant/razorpay/verify', {
              invoice_id: selectedInv.invoice.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success('Payment received');
            load();
            setSelectedInv(null);
          } catch (err) { toast.error(err.message); }
        },
      };
      // eslint-disable-next-line no-undef
      const rz = new window.Razorpay(options);
      rz.open();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="space-y-5" data-testid="billing-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-extrabold text-3xl text-rozops-navy tracking-tight">Billing</h1>
        <button onClick={() => setShowGen(true)} data-testid="gen-invoice-btn" className="bg-rozops-blue text-white rounded-xl px-4 py-2.5 font-semibold shadow-soft inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Generate invoice</button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-soft overflow-x-auto">
        <table className="w-full">
          <thead className="bg-rozops-bg border-b border-gray-100">
            <tr className="text-left text-xs uppercase tracking-wider text-rozops-text-muted font-bold">
              <th className="py-3 px-6">Invoice #</th><th className="py-3 px-6">Customer</th><th className="py-3 px-6">Period</th><th className="py-3 px-6">Total</th><th className="py-3 px-6">Due</th><th className="py-3 px-6">Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-rozops-text-muted">No invoices yet.</td></tr>}
            {invoices.map(i => (
              <tr key={i.id} className="border-b border-gray-50" data-testid={`invoice-${i.id}`}>
                <td className="py-4 px-6 font-semibold">{i.invoice_number}</td>
                <td className="py-4 px-6 text-sm">{i.customer?.full_name}</td>
                <td className="py-4 px-6 text-sm text-rozops-text-muted">{i.billing_start_date} → {i.billing_end_date}</td>
                <td className="py-4 px-6 text-sm font-semibold">₹{Number(i.total_amount).toFixed(2)}</td>
                <td className="py-4 px-6 text-sm text-red-500 font-semibold">₹{Number(i.due_amount).toFixed(2)}</td>
                <td className="py-4 px-6"><span className={`px-2.5 py-1 rounded-full text-xs font-bold ${i.status === 'paid' ? 'bg-rozops-green-light text-rozops-green' : 'bg-amber-50 text-amber-700'}`}>{i.status}</span></td>
                <td className="py-4 px-6"><button onClick={() => view(i.id)} className="text-rozops-blue"><Eye className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showGen && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => setShowGen(false)}>
          <div className="bg-white rounded-2xl shadow-medium p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading font-bold text-xl text-rozops-navy mb-4">Generate invoice</h2>
            <form onSubmit={generate} className="space-y-3">
              <select required className={inputCls} value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="inv-customer">
                <option value="">Select customer</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase font-bold text-rozops-text-muted">From</label>
                  <input required type="date" className={inputCls} value={form.billing_start_date} onChange={(e) => setForm({ ...form, billing_start_date: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase font-bold text-rozops-text-muted">To</label>
                  <input required type="date" className={inputCls} value={form.billing_end_date} onChange={(e) => setForm({ ...form, billing_end_date: e.target.value })} />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowGen(false)} className="px-4 py-2 rounded-xl text-rozops-text-muted">Cancel</button>
                <button type="submit" data-testid="inv-submit" className="bg-rozops-blue text-white rounded-xl px-4 py-2 font-semibold">Generate</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedInv && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => setSelectedInv(null)}>
          <div className="bg-white rounded-2xl shadow-medium p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-heading font-bold text-xl text-rozops-navy">{selectedInv.invoice.invoice_number}</h2>
              <FileText className="w-5 h-5 text-rozops-blue" />
            </div>
            <div className="text-sm text-rozops-text-muted">{selectedInv.invoice.customer?.full_name} · {selectedInv.invoice.billing_start_date} → {selectedInv.invoice.billing_end_date}</div>
            <table className="w-full mt-4 text-sm">
              <thead className="text-xs uppercase text-rozops-text-muted">
                <tr><th className="text-left py-2">Date</th><th className="text-left">Item</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Amount</th></tr>
              </thead>
              <tbody>
                {selectedInv.items.map(it => (
                  <tr key={it.id} className="border-t border-gray-100">
                    <td className="py-2">{it.delivery_date}</td><td>{it.product?.name}</td><td className="text-right">{Number(it.quantity).toFixed(2)}</td><td className="text-right">₹{Number(it.rate).toFixed(2)}</td><td className="text-right">₹{Number(it.amount).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 text-right space-y-1 text-sm">
              <div>Subtotal: <span className="font-semibold">₹{Number(selectedInv.invoice.subtotal).toFixed(2)}</span></div>
              <div>Previous balance: <span className="font-semibold">₹{Number(selectedInv.invoice.previous_balance).toFixed(2)}</span></div>
              <div className="text-lg font-heading font-extrabold text-rozops-navy">Total: ₹{Number(selectedInv.invoice.total_amount).toFixed(2)}</div>
              <div className="text-red-500 font-semibold">Due: ₹{Number(selectedInv.invoice.due_amount).toFixed(2)}</div>
            </div>
            <div className="mt-6 flex flex-wrap gap-2 justify-end">
              <button onClick={generatePdf} disabled={pdfBusy} data-testid="inv-pdf" className="bg-white border-2 border-rozops-blue-light hover:border-rozops-blue text-rozops-text rounded-xl px-4 py-2 font-semibold inline-flex items-center gap-2 disabled:opacity-50"><Download className="w-4 h-4" /> {pdfBusy ? 'Generating…' : 'Download PDF'}</button>
              <button onClick={shareWhatsapp} data-testid="inv-whatsapp" className="bg-rozops-green hover:bg-rozops-green-dark text-white rounded-xl px-4 py-2 font-semibold inline-flex items-center gap-2"><Share2 className="w-4 h-4" /> Share on WhatsApp</button>
              {Number(selectedInv.invoice.due_amount) > 0 && (
                <button onClick={collectRazorpay} data-testid="inv-razorpay" className="bg-rozops-blue hover:bg-rozops-blue-dark text-white rounded-xl px-4 py-2 font-semibold inline-flex items-center gap-2"><IndianRupee className="w-4 h-4" /> Collect via Razorpay</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
